/**
 * The shape the opt-in form edits, and how it maps to the contract.
 *
 * The form keeps **every language at once**, not the language currently on screen: switching tabs
 * only changes which slice is rendered, so nothing typed is ever thrown away. The payload is
 * assembled at save time — base columns in English, the rest as `translations`, exactly as
 * `ADMIN · Opt-in` describes.
 *
 * @module Pages/BUs/OptInFeatures
 */

import type {
  OptInEntryBadge,
  OptInFeature,
  OptInFeatureAppearance,
  OptInFeatureCreatePayload,
  OptInFeatureToasts,
  OptInFeatureTranslation,
  OptInFeatureUpdatePayload,
  OptInLanguage,
  OptInToastType,
} from '../Types/OptInFeatures.types';
import { OPT_IN_LANGUAGES } from '../Types/OptInFeatures.types';

/** The fifteen texts, as the form holds them: always a string, never `null`. */
export interface OptInTextsDraft {
  name: string;
  description: string;
  enterCta: string;
  exitCta: string;
  badgeTooltip: string;
  confirmTitle: string;
  confirmBody: string;
  confirmCta: string;
  enableToastTitle: string;
  enableToastBody: string;
  optOutTitle: string;
  optOutBody: string;
  optOutCta: string;
  disableToastTitle: string;
  disableToastBody: string;
}

/**
 * The entry point's appearance, as the form holds it — **outside `texts`, and that is the point**.
 *
 * Four of these five have no language: an icon variant and a badge enum read the same in every
 * one, so keeping them per language would invite three tabs to disagree about a glyph. Only
 * `badgeTooltip` is a sentence, and it lives with the other texts.
 *
 * `null` survives into the draft instead of becoming `''`, because here `null` carries meaning:
 * "the dashboard decides".
 */
export type OptInAppearanceDraft = OptInFeatureAppearance;

/**
 * The default the BFF also defaults to: nothing decided, and the invitation shown.
 *
 * A brand-new cadastro saved without touching this block leaves the dashboard exactly as it
 * renders today.
 */
export const EMPTY_APPEARANCE: OptInAppearanceDraft = {
  enterIcon: null,
  exitIcon: null,
  enterBadge: null,
  exitBadge: null,
  enterVisible: true,
};

/**
 * The two toast switches, as the form holds them — **outside `texts`, like the appearance**.
 *
 * Neither has a language: which language somebody reads in does not decide whether an alert fires.
 * The four sentences the toasts carry are in {@link OptInTextsDraft}, translated like the rest.
 *
 * `null` is the off position and stays `null` in the draft rather than becoming `''`: here it
 * carries meaning — "this side of the toggle says nothing".
 */
export type OptInToastsDraft = OptInFeatureToasts;

/** Neither side says anything, which is what every feature registered so far carries. */
export const EMPTY_TOASTS: OptInToastsDraft = {
  enableToastType: null,
  disableToastType: null,
};

/**
 * Toast choices, in the order the select offers them.
 *
 * `null` first because it is the default and the off position — **this select is the switch**, so
 * "Nenhum" has to be a visible option and not something you reach by clearing a field.
 */
export const OPT_IN_TOAST_CHOICES: { value: OptInToastType | null; label: string }[] = [
  { value: null, label: 'Nenhum — não dispara toast' },
  { value: 'INFO', label: 'Informação' },
  { value: 'SUCCESS', label: 'Sucesso' },
  { value: 'WARNING', label: 'Aviso' },
  { value: 'ERROR', label: 'Erro' },
];

/**
 * The two sides of the toggle, each naming the three fields it owns.
 *
 * One table, read by the form (which renders the block under the matching surface) and by the
 * problem list (which checks a type against its title). Two copies of the pairing is how one of
 * them ends up validating the enable title against the disable type.
 */
export interface OptInToastSide {
  id: 'enable' | 'disable';
  /** Which of the three surfaces the block is rendered under. */
  surface: 'confirm' | 'optOut';
  typeField: keyof OptInToastsDraft;
  titleField: keyof OptInTextsDraft;
  bodyField: keyof OptInTextsDraft;
  title: string;
  hint: string;
  /** Who is looking at the screen when it fires — named in the pendência and in the preview. */
  audience: string;
}

export const OPT_IN_TOAST_SIDES: OptInToastSide[] = [
  {
    id: 'enable',
    surface: 'confirm',
    typeField: 'enableToastType',
    titleField: 'enableToastTitle',
    bodyField: 'enableToastBody',
    title: 'Toast depois de ligar',
    hint: 'Aparece no dashboard logo depois que a pessoa confirma neste modal. Opcional: em «Nenhum» não dispara nada, que é o padrão.',
    audience: 'quem acabou de ligar a funcionalidade',
  },
  {
    id: 'disable',
    surface: 'optOut',
    typeField: 'disableToastType',
    titleField: 'disableToastTitle',
    bodyField: 'disableToastBody',
    title: 'Toast depois de desligar',
    hint: 'Aparece logo depois que a pessoa sai por este modal. Independente do de cima, e disparado pelo desligamento em si — não pelo envio do questionário de saída, que pode falhar.',
    audience: 'quem acabou de desligar a funcionalidade',
  },
];

/** Badge choices, in the order the select offers them. `null` first, since it is the default. */
export const OPT_IN_BADGE_CHOICES: { value: OptInEntryBadge | null; label: string }[] = [
  { value: null, label: 'Deixar o dashboard decidir' },
  { value: 'NONE', label: 'Sem selo' },
  { value: 'BETA', label: 'Beta' },
  { value: 'NEW', label: 'New' },
];

/**
 * How wide a field is, in columns of the twelve-column grid each surface lays out.
 *
 * A button label is two words; giving it the width of a paragraph invites someone to write a
 * paragraph into it. The widths are the first thing that says how long a text should be.
 */
export type OptInFieldWidth = 'cta' | 'title' | 'full';

export interface OptInTextField {
  name: keyof OptInTextsDraft;
  label: string;
  width: OptInFieldWidth;
  multiline?: boolean;
}

export interface OptInTextSurface {
  id: 'entry' | 'confirm' | 'optOut';
  /** ①②③, so the three surfaces read as steps of one journey rather than three unrelated cards. */
  index: string;
  title: string;
  hint: string;
  fields: OptInTextField[];
}

/** The three surfaces the ten texts belong to. Drives the form's grouping and nothing else. */
export const OPT_IN_TEXT_SURFACES: OptInTextSurface[] = [
  {
    id: 'entry',
    index: '①',
    title: 'Ponto de entrada',
    hint: 'O convite na sidebar. É um botão só, com dois rótulos: um para ligar, outro para voltar atrás.',
    fields: [
      { name: 'name', label: 'Nome', width: 'title' },
      { name: 'description', label: 'Descrição', width: 'full', multiline: true },
      { name: 'enterCta', label: 'Rótulo · desligada', width: 'cta' },
      { name: 'exitCta', label: 'Rótulo · ligada', width: 'cta' },
      { name: 'badgeTooltip', label: 'Tooltip do selo', width: 'title' },
    ],
  },
  {
    id: 'confirm',
    index: '②',
    title: 'Modal de ativar',
    hint: 'O que a pessoa lê antes de ligar a funcionalidade.',
    fields: [
      { name: 'confirmTitle', label: 'Título', width: 'title' },
      { name: 'confirmBody', label: 'Texto', width: 'full', multiline: true },
      { name: 'confirmCta', label: 'Rótulo do botão', width: 'cta' },
    ],
  },
  {
    id: 'optOut',
    index: '③',
    title: 'Modal de desativar',
    hint: 'O que a pessoa lê ao voltar para o comportamento antigo. O que ela RESPONDE ali é o questionário de saída, que é uma pesquisa e vive no módulo Pesquisas.',
    fields: [
      { name: 'optOutTitle', label: 'Título', width: 'title' },
      { name: 'optOutBody', label: 'Texto', width: 'full', multiline: true },
      { name: 'optOutCta', label: 'Rótulo do botão', width: 'cta' },
    ],
  },
];

export interface OptInFeatureFormData {
  tenantSlug: string;
  key: string;
  /** Ids of the tenant's use cases. Empty is "every use case", which is what the BFF means by it. */
  useCaseIds: string[];
  isActive: boolean;
  texts: Record<OptInLanguage, OptInTextsDraft>;
  /** The entry point's icons, badges and visibility — one set, not one per language. */
  appearance: OptInAppearanceDraft;
  /** Whether each side of the toggle raises a toast — one set, not one per language. */
  toasts: OptInToastsDraft;
}

export const EMPTY_TEXTS: OptInTextsDraft = {
  name: '',
  description: '',
  enterCta: '',
  exitCta: '',
  badgeTooltip: '',
  confirmTitle: '',
  confirmBody: '',
  confirmCta: '',
  enableToastTitle: '',
  enableToastBody: '',
  optOutTitle: '',
  optOutBody: '',
  optOutCta: '',
  disableToastTitle: '',
  disableToastBody: '',
};

export const createEmptyFormData = (tenantSlug = ''): OptInFeatureFormData => ({
  tenantSlug,
  key: '',
  useCaseIds: [],
  isActive: true,
  texts: { en: { ...EMPTY_TEXTS }, pt: { ...EMPTY_TEXTS }, es: { ...EMPTY_TEXTS } },
  appearance: { ...EMPTY_APPEARANCE },
  toasts: { ...EMPTY_TOASTS },
});

/**
 * The shape the key input enforces: lowercase and hyphenated.
 *
 * `mdm_menu`, `new-menu` and `mdmMenu` are three different 404s in the dashboard, so the field
 * does not let them be typed in the first place.
 */
export const slugifyKey = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-/, '');

const KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export interface OptInFormErrors {
  key?: string;
  tenantSlug?: string;
  name?: string;
}

/**
 * What must be true before the BFF is asked.
 *
 * Only the base language is required: the approval of a missing translation is the BFF's job, and
 * blocking a draft here would stop someone from saving half a cadastro.
 */
export const validateFormData = (form: OptInFeatureFormData): OptInFormErrors => {
  const errors: OptInFormErrors = {};

  if (!form.tenantSlug) errors.tenantSlug = 'Escolha a BU dona da funcionalidade.';

  if (!form.key.trim()) {
    errors.key = 'A chave é obrigatória — é por ela que o dashboard reconhece a funcionalidade.';
  } else if (!KEY_PATTERN.test(form.key)) {
    errors.key = 'Use apenas letras minúsculas, números e hífen (ex.: mdm-menu).';
  }

  if (!form.texts.en.name.trim()) {
    errors.name = 'O nome em inglês é obrigatório: é a coluna base da tabela.';
  }

  return errors;
};

export const hasErrors = (errors: OptInFormErrors): boolean => Object.keys(errors).length > 0;

const toNullable = (value: string): string | null => {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
};

const translationFor = (
  form: OptInFeatureFormData,
  language: OptInLanguage,
): OptInFeatureTranslation | null => {
  const texts = form.texts[language];
  const entries = Object.entries(texts).filter(([, value]) => value.trim() !== '');

  if (entries.length === 0) return null;

  return {
    language,
    ...Object.fromEntries(entries.map(([field, value]) => [field, value.trim()])),
  };
};

const buildTranslations = (form: OptInFeatureFormData): OptInFeatureTranslation[] =>
  OPT_IN_LANGUAGES.filter(language => language !== 'en')
    .map(language => translationFor(form, language))
    .filter((translation): translation is OptInFeatureTranslation => translation !== null);

/**
 * English fills the base columns; `pt` and `es` become `translations`.
 *
 * The appearance and the two toast types ride along as base columns too — an icon, a badge and an
 * alert level have no language, and the sentences they carry (`badgeTooltip`, the four toast
 * texts) are already among the texts, so they need nothing special here. `null` is sent explicitly
 * rather than omitted: it means "the dashboard decides" for the appearance and "this side says
 * nothing" for a toast, and on a `PATCH` an omitted field would instead leave whatever was there —
 * which is how somebody who switched a toast off would find it still firing.
 */
export const buildCreatePayload = (form: OptInFeatureFormData): OptInFeatureCreatePayload => {
  const base = form.texts.en;

  return {
    tenantSlug: form.tenantSlug,
    key: form.key.trim(),
    useCaseIds: form.useCaseIds,
    name: base.name.trim(),
    description: toNullable(base.description),
    enterCta: toNullable(base.enterCta),
    exitCta: toNullable(base.exitCta),
    badgeTooltip: toNullable(base.badgeTooltip),
    enterIcon: form.appearance.enterIcon,
    exitIcon: form.appearance.exitIcon,
    enterBadge: form.appearance.enterBadge,
    exitBadge: form.appearance.exitBadge,
    enterVisible: form.appearance.enterVisible,
    confirmTitle: toNullable(base.confirmTitle),
    confirmBody: toNullable(base.confirmBody),
    confirmCta: toNullable(base.confirmCta),
    enableToastType: form.toasts.enableToastType,
    enableToastTitle: toNullable(base.enableToastTitle),
    enableToastBody: toNullable(base.enableToastBody),
    optOutTitle: toNullable(base.optOutTitle),
    optOutBody: toNullable(base.optOutBody),
    optOutCta: toNullable(base.optOutCta),
    disableToastType: form.toasts.disableToastType,
    disableToastTitle: toNullable(base.disableToastTitle),
    disableToastBody: toNullable(base.disableToastBody),
    isActive: form.isActive,
    translations: buildTranslations(form),
  };
};

/** Same payload without `key` and `tenantSlug`, neither of which can move after creation. */
export const buildUpdatePayload = (form: OptInFeatureFormData): OptInFeatureUpdatePayload => {
  const { key: _key, tenantSlug: _tenantSlug, ...rest } = buildCreatePayload(form);

  return rest;
};

/** Seeds the form from an existing feature, spreading its translations back into the tabs. */
export const mapFeatureToFormData = (feature: OptInFeature): OptInFeatureFormData => {
  const texts: Record<OptInLanguage, OptInTextsDraft> = {
    en: {
      name: feature.name ?? '',
      description: feature.description ?? '',
      enterCta: feature.enterCta ?? '',
      exitCta: feature.exitCta ?? '',
      badgeTooltip: feature.badgeTooltip ?? '',
      confirmTitle: feature.confirmTitle ?? '',
      confirmBody: feature.confirmBody ?? '',
      confirmCta: feature.confirmCta ?? '',
      enableToastTitle: feature.enableToastTitle ?? '',
      enableToastBody: feature.enableToastBody ?? '',
      optOutTitle: feature.optOutTitle ?? '',
      optOutBody: feature.optOutBody ?? '',
      optOutCta: feature.optOutCta ?? '',
      disableToastTitle: feature.disableToastTitle ?? '',
      disableToastBody: feature.disableToastBody ?? '',
    },
    pt: { ...EMPTY_TEXTS },
    es: { ...EMPTY_TEXTS },
  };

  (feature.translations ?? []).forEach(translation => {
    const language = translation.language;
    if (language === 'en') return;

    (Object.keys(EMPTY_TEXTS) as (keyof OptInTextsDraft)[]).forEach(field => {
      texts[language][field] = translation[field] ?? '';
    });
  });

  return {
    tenantSlug: feature.tenantSlug,
    key: feature.key,
    useCaseIds: (feature.useCases ?? []).map(useCase => useCase.id),
    isActive: feature.isActive,
    texts,
    /*
      `??` and not `||`: `enterVisible: false` is a real answer and must not be read as "unset".
      A BFF older than 4.2.0 answers without these, and then the defaults are what it behaves as.
    */
    appearance: {
      enterIcon: feature.enterIcon ?? null,
      exitIcon: feature.exitIcon ?? null,
      enterBadge: feature.enterBadge ?? null,
      exitBadge: feature.exitBadge ?? null,
      enterVisible: feature.enterVisible ?? true,
    },
    /*
      `??` again, and for the same reason as above: a BFF older than 4.3.0 answers without these,
      and then the feature behaves as if neither side said anything — which it does.
    */
    toasts: {
      enableToastType: feature.enableToastType ?? null,
      disableToastType: feature.disableToastType ?? null,
    },
  };
};

/** How many of the fifteen texts a language has, for the badge on each tab. */
export const countFilledTexts = (texts: OptInTextsDraft): number =>
  Object.values(texts).filter(value => value.trim() !== '').length;
